import * as React from "react";
import { trackEvent } from "../lib/analytics";

const STORAGE_KEY = "mailingListCTA";
const VISITS_BEFORE_MAXIMIZE = 3; // default to 3 if unset

interface CTAState {
  visitCount: number;
  dismissed: boolean;
  submitted: boolean;
}

const getStoredState = (): CTAState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {}
  return { visitCount: 0, dismissed: false, submitted: false };
};

const setStoredState = (state: CTAState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {}
};

export interface MailingListCTARef {
  open: () => void;
}

export const MailingListCTA = React.forwardRef<MailingListCTARef>((_, ref) => {
  // Always visible, don't use isVisible
  const [isMinimized, setIsMinimized] = React.useState(true);
  const [submitted, setSubmitted] = React.useState(false);
  const [email, setEmail] = React.useState("");

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setStoredState({
        ...getStoredState(),
        dismissed: false,
        submitted: false,
      });
      setIsMinimized(false);
      setSubmitted(false);
      setEmail("");
    },
  }));

  React.useEffect(() => {
    const state = getStoredState();
    let { visitCount, dismissed, submitted: alreadySubmitted } = state;

    visitCount += 1;
    setStoredState({ ...state, visitCount });

    if (
      !dismissed &&
      !alreadySubmitted &&
      visitCount >= VISITS_BEFORE_MAXIMIZE
    ) {
      setIsMinimized(false); // maximize after N visits to nudge user
    } else {
      setIsMinimized(true); // otherwise always minimized by default
    }

    // In case user already submitted in previous session
    if (alreadySubmitted && !submitted) {
      setSubmitted(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsMinimized(true);
    setStoredState({ ...getStoredState(), dismissed: true });
  };

  const state = getStoredState();

  // Use visiaul hiding, to allwo user to open the CTA again via a ref from elsewhere in the app
  const shouldHide = state.dismissed || state.submitted;

  return (
    <div
      className={`transition-all duration-300 ease-in-out
        ${isMinimized ? "" : "md:w-80 w-[calc(100%-1rem)]"} ${shouldHide ? "hidden" : ""}`}
      style={isMinimized ? {} : { maxWidth: "320px" }}
    >
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-theme-color border border-theme-color p-2 px-3 md:px-3 hover:opacity-75 flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <span className="text-sm hidden md:inline">SUBSCRIBE</span>
        </button>
      ) : (
        <div className="bg-theme-color border border-theme-color">
          <div className="flex items-center justify-between p-2 border-b-2 border-dashed border-theme-color">
            <span className="font-bold px-2">STAY UPDATED</span>
            <div className="flex gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1 hover:opacity-50"
                title="Minimize"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                </svg>
              </button>
              <button
                onClick={handleDismiss}
                className="p-1 hover:opacity-50"
                title="Dismiss"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-3">
            {submitted ? (
              <div className="text-center py-2">
                <p className="font-bold">Thank you!</p>
                <p className="text-sm">You're on the list.</p>
              </div>
            ) : (
              <>
                <p className="text-sm mb-3">
                  Get updates on new artifacts and features from the African
                  Artifacts Index.
                </p>
                <form
                  name="mailing-list"
                  {...{ netlify: "true" }}
                >
                  <input type="hidden" name="form-name" value="mailing-list" />
                  <p className="hidden">
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  <div className="flex flex-col gap-2">
                    {/* Hidden input for fixing Netlify forms despite non-statically generated site */}
                    <input type="hidden" name="form-name" value="contact" />

                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.currentTarget.value)
                      }
                      placeholder="your@email.com"
                      required
                      className="border p-2 w-full"
                    />
                    <button
                      type="submit"
                      className="border-theme-color text-theme-color hover:bg-theme-color border p-2 border-dashed w-full"
                      onClick={_ => {
                        trackEvent("subscribe_click");
                        window.open("https://suruleredotdev.substack.com?utm_source=navbar&utm_medium=web", "_blank");
                      }}
                    >
                      SUBSCRIBE
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

MailingListCTA.displayName = "MailingListCTA";
